import { useMsal } from '@azure/msal-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  apiFetch,
  apiGetBlob,
  apiUpload,
  type ApplicationDetail,
  type ApplicationSummary,
  type Recommendation,
} from './client';

export function useApplications(jobId?: string) {
  const { instance } = useMsal();
  const q = jobId ? `?jobId=${encodeURIComponent(jobId)}` : '';
  return useQuery({
    queryKey: ['applications', jobId ?? 'all'],
    queryFn: () => apiFetch<ApplicationSummary[]>(instance, `/api/applications${q}`),
  });
}

export function useApplication(id: string | undefined) {
  const { instance } = useMsal();
  return useQuery({
    queryKey: ['application', id],
    queryFn: () => apiFetch<ApplicationDetail>(instance, `/api/applications/${id}`),
    enabled: Boolean(id),
  });
}

export function useProposeApplication(jobId: string) {
  const { instance } = useMsal();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: FormData) =>
      apiUpload<ApplicationSummary>(instance, `/api/jobs/${jobId}/applications`, form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['applications'] });
      qc.invalidateQueries({ queryKey: ['jobs', jobId] });
    },
  });
}

export function useViewCv() {
  const { instance } = useMsal();
  return async (applicationId: string) => {
    const blob = await apiGetBlob(instance, `/api/applications/${applicationId}/cv`);
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };
}

function useAppInvalidate(id: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['application', id] });
    qc.invalidateQueries({ queryKey: ['applications'] });
  };
}

export function useReplaceCv(id: string) {
  const { instance } = useMsal();
  const invalidate = useAppInvalidate(id);
  return useMutation({
    mutationFn: (form: FormData) => apiUpload(instance, `/api/applications/${id}/cv`, form, 'PUT'),
    onSuccess: invalidate,
  });
}

interface VoteInput { decision: 'APPROVED' | 'DENIED'; comment?: string }

export function useCastVote(id: string) {
  const { instance } = useMsal();
  const invalidate = useAppInvalidate(id);
  return useMutation({
    mutationFn: (input: VoteInput) =>
      apiFetch(instance, `/api/applications/${id}/reviews`, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

export function useEditVote(id: string) {
  const { instance } = useMsal();
  const invalidate = useAppInvalidate(id);
  return useMutation({
    mutationFn: (input: VoteInput) =>
      apiFetch(instance, `/api/applications/${id}/reviews`, { method: 'PUT', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

export function useAdvance(id: string) {
  const { instance } = useMsal();
  const invalidate = useAppInvalidate(id);
  return useMutation({
    mutationFn: () => apiFetch(instance, `/api/applications/${id}/advance`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

export function useReject(id: string) {
  const { instance } = useMsal();
  const invalidate = useAppInvalidate(id);
  return useMutation({
    mutationFn: (reason?: string) =>
      apiFetch(instance, `/api/applications/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: invalidate,
  });
}

export function useHire(id: string) {
  const { instance } = useMsal();
  const invalidate = useAppInvalidate(id);
  return useMutation({
    mutationFn: () => apiFetch(instance, `/api/applications/${id}/hire`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

export interface FeedbackInput {
  rating: number;
  recommendation: Recommendation;
  strengths?: string;
  concerns?: string;
  notes?: string;
}

export function useSubmitFeedback(id: string) {
  const { instance } = useMsal();
  const invalidate = useAppInvalidate(id);
  return useMutation({
    mutationFn: (input: FeedbackInput) =>
      apiFetch(instance, `/api/applications/${id}/feedback`, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

export function useEditFeedback(id: string) {
  const { instance } = useMsal();
  const invalidate = useAppInvalidate(id);
  return useMutation({
    mutationFn: (input: FeedbackInput) =>
      apiFetch(instance, `/api/applications/${id}/feedback`, { method: 'PUT', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}
